import { tr } from '@/lib/i18n-core';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import {
  cn, dateOnly, dateTime, display, downloadCsv, humanize, money, toIsoDate, todayIso, type CsvColumn,
} from '@/lib/utils';
import { DataTable, FilterBar, FilterField, StatTile, type DataTableColumn } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Select, TableSkeleton } from '@/components/ui/primitives';
import { CsvExportButton } from './CsvExportButton';
import type { QueryParams } from './paging-export';
import {
  allInputRates,
  ledgerTieLine,
  netInputTaxMinor,
  voidedFigureAt,
  type ExpiryLedgerTie,
} from './reports-view';

const PAGE_SIZE = 50;
const MAX_PERIOD_DAYS = 366;
const MS_PER_DAY = 86_400_000;
const DEFAULT_PERIOD_DAYS = 30;
const EXPIRY_PERIOD_DAYS = 90;

type Column<T> = DataTableColumn<T>;

interface AppliedFilters<T> {
  values: T;
  runs: number;
}

function isoDaysBack(days: number): string {
  return toIsoDate(new Date(Date.now() - days * MS_PER_DAY));
}

function periodProblem(from: string, to: string): string | null {
  if (!from || !to) return 'Choose both dates.';
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY) + 1;
  if (days < 1) return 'The period ends before it starts.';
  if (days > MAX_PERIOD_DAYS) return `Ask for at most ${MAX_PERIOD_DAYS} days at a time.`;
  return null;
}

function quantity(value: number): string {
  return new Intl.NumberFormat(display.locale, { maximumFractionDigits: 3 }).format(value);
}

function quantityOrDash(value: number): string {
  return value === 0 ? '—' : quantity(value);
}

function percent(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}

function plainAmount(minor: number): string {
  return (minor / 100).toFixed(2);
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

const RUN_POSITION_IN_KEY = 3;

function keepWhilePaging<T>(runs: number | undefined) {
  return (previousData: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined) =>
    previousQuery?.queryKey[RUN_POSITION_IN_KEY] === runs ? previousData : undefined;
}

function useReportFilters<T extends object>(initial: () => T) {
  const [draft, setDraft] = useState<T>(initial);
  const [applied, setApplied] = useState<AppliedFilters<T> | null>(null);
  const [offset, setOffset] = useState(0);

  const change = (patch: Partial<T>) => setDraft((current) => ({ ...current, ...patch }));
  const run = () => {
    setApplied((current) => ({ values: draft, runs: (current?.runs ?? 0) + 1 }));
    setOffset(0);
  };

  return { draft, change, applied, run, offset, setOffset };
}

interface StoreOption {
  id: string;
  name: string;
  branch: { name: string } | null;
}

function StoreFilter({ value, onChange }: { value: string; onChange: (storeId: string) => void }) {
  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });

  return (
    <FilterField label={tr('pharmacy', 'store')} className="w-60">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{tr('pharmacy', 'all_stores')}</option>
        {(stores.data ?? []).map((store) => (
          <option key={store.id} value={store.id}>
            {store.branch ? `${store.branch.name} — ${store.name}` : store.name}
          </option>
        ))}
      </Select>
      {stores.error && <span className="text-xs text-destructive">{describeError(stores.error)}</span>}
    </FilterField>
  );
}

function ReportFilterBar({
  from,
  to,
  onChange,
  running,
  onRun,
  trailing,
  children,
}: {
  from: string;
  to: string;
  onChange: (patch: { from?: string; to?: string }) => void;
  running: boolean;
  onRun: () => void;
  trailing?: ReactNode;
  children?: ReactNode;
}) {
  const problem = periodProblem(from, to);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!problem && !running) onRun();
  };

  return (
    <form onSubmit={submit} className="mb-4 space-y-2">
      <FilterBar className="mb-0" trailing={trailing}>
        <FilterField label={tr('pharmacy', 'from')} className="w-40">
          <DatePicker value={from} onChange={(value) => onChange({ from: value })} />
        </FilterField>
        <FilterField label={tr('pharmacy', 'to')} className="w-40">
          <DatePicker value={to} onChange={(value) => onChange({ to: value })} />
        </FilterField>
        {children}
        <Button type="submit" loading={running} disabled={problem !== null}>
          {tr('pharmacy', 'run')}
        </Button>
      </FilterBar>
      {problem && <Alert variant="warning">{problem}</Alert>}
    </form>
  );
}

function ReportBody({
  started,
  loading,
  error,
  dimmed,
  children,
}: {
  started: boolean;
  loading: boolean;
  error: unknown;
  dimmed?: boolean;
  children: ReactNode;
}) {
  if (!started) {
    return (
      <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
        {tr('pharmacy', 'choose_the_period_and_press_run')}
      </div>
    );
  }
  if (loading) {
    return (
      <TableSkeleton />
    );
  }
  if (error) {
    return (
      <Alert variant="destructive" title={tr('pharmacy', 'the_report_could_not_be_produced')}>
        {describeError(error)}
      </Alert>
    );
  }
  return <div className={cn(dimmed && 'opacity-60 transition-opacity')}>{children}</div>;
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </div>
      {children}
    </section>
  );
}

function ExportButton<T>({
  filename,
  rows,
  columns,
}: {
  filename: string;
  rows: readonly T[];
  columns: readonly CsvColumn<T>[];
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={rows.length === 0}
      onClick={() => downloadCsv(filename, rows, columns)}
    >
      <Download className="h-4 w-4" /> {tr('pharmacy', 'export_csv')}
    </Button>
  );
}

function Pager({
  offset,
  total,
  shown,
  busy,
  onChange,
}: {
  offset: number;
  total: number;
  shown: number;
  busy: boolean;
  onChange: (offset: number) => void;
}) {
  const first = shown === 0 ? 0 : offset + 1;
  const last = offset + shown;

  return (
    <div className="flex w-full items-center justify-between text-sm text-muted-foreground">
      <span>
        {first}–{last} {tr('pharmacy', 'of')} {total}
      </span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset === 0 || busy}
          onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}
        >
          {tr('pharmacy', 'previous')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset + PAGE_SIZE >= total || busy}
          onClick={() => onChange(offset + PAGE_SIZE)}
        >
          {tr('pharmacy', 'next')}
        </Button>
      </div>
    </div>
  );
}

function Amount({ minor, strong, tone }: { minor: number; strong?: boolean; tone?: 'bad' }) {
  return (
    <span className={cn('tabular', strong && 'font-semibold', tone === 'bad' && 'text-destructive')}>
      {money(minor)}
    </span>
  );
}

interface VatRateRow {
  rate: number;
  taxableMinor: number;
  taxMinor: number;
  documents: number;
}

interface VatOutputRow extends VatRateRow {
  returnedTaxableMinor: number;
  returnedTaxMinor: number;
}

interface VatTotals {
  taxableMinor: number;
  taxMinor: number;
}

interface VatLedgerSource {
  source: string;
  class: 'output' | 'input' | 'other';
  debitMinor: number;
  creditMinor: number;
  netCreditMinor: number;
}

interface VatReturn {
  from: string;
  to: string;
  output: VatOutputRow[];
  outputTotals: VatTotals;
  input: VatRateRow[];
  inputTotals: VatTotals;
  purchaseReturns: VatRateRow[];
  purchaseReturnTotals: VatTotals;
  voidedReceipts?: VatRateRow[];
  voidedReceiptTotals?: VatTotals;
  documents: { outputTaxMinor: number; inputTaxMinor: number; netVatPayableMinor: number };
  ledger: {
    account: string;
    openingBalanceMinor: number;
    outputTaxMinor: number;
    inputTaxMinor: number;
    otherMinor: number;
    movementMinor: number;
    closingBalanceMinor: number;
    netVatPayableMinor: number;
    sources: VatLedgerSource[];
  };
  reconciliation: { outputDifferenceMinor: number; inputDifferenceMinor: number };
}

interface OutputTableRow {
  key: string;
  label: string;
  documents: number | null;
  taxableMinor: number;
  taxMinor: number;
  returnedTaxableMinor: number;
  returnedTaxMinor: number;
  total: boolean;
}

interface InputTableRow {
  key: string;
  label: string;
  receipts: number | null;
  receivedTaxableMinor: number;
  receivedTaxMinor: number;
  returnNotes: number | null;
  returnedTaxableMinor: number;
  returnedTaxMinor: number;
  voidedTaxableMinor: number;
  voidedTaxMinor: number;
  total: boolean;
}

interface VoidedTableRow {
  key: string;
  label: string;
  receipts: number | null;
  taxableMinor: number;
  taxMinor: number;
  total: boolean;
}

interface ComparisonRow {
  label: string;
  documentsMinor: number;
  ledgerMinor: number;
  differenceMinor: number;
  strong: boolean;
}

interface VatCsvRow {
  section: string;
  line: string;
  documents: number | null;
  taxableMinor: number | null;
  taxMinor: number | null;
  debitMinor: number | null;
  creditMinor: number | null;
}

const optionalAmount = (minor: number | null): string => (minor === null ? '' : plainAmount(minor));

const VAT_CSV_COLUMNS: CsvColumn<VatCsvRow>[] = [
  { header: 'Section', value: (row) => row.section },
  { header: 'Line', value: (row) => row.line },
  { header: 'Documents', value: (row) => row.documents },
  { header: 'Taxable', value: (row) => optionalAmount(row.taxableMinor) },
  { header: 'VAT', value: (row) => optionalAmount(row.taxMinor) },
  { header: 'Debit', value: (row) => optionalAmount(row.debitMinor) },
  { header: 'Credit', value: (row) => optionalAmount(row.creditMinor) },
];

function outputTableRows(report: VatReturn): OutputTableRow[] {
  const rows: OutputTableRow[] = report.output.map((row) => ({
    key: String(row.rate),
    label: `${row.rate}%`,
    documents: row.documents,
    taxableMinor: row.taxableMinor,
    taxMinor: row.taxMinor,
    returnedTaxableMinor: row.returnedTaxableMinor,
    returnedTaxMinor: row.returnedTaxMinor,
    total: false,
  }));
  rows.push({
    key: 'total',
    label: tr('pharmacy', 'total'),
    documents: null,
    taxableMinor: report.outputTotals.taxableMinor,
    taxMinor: report.outputTotals.taxMinor,
    returnedTaxableMinor: report.output.reduce((sum, row) => sum + row.returnedTaxableMinor, 0),
    returnedTaxMinor: report.output.reduce((sum, row) => sum + row.returnedTaxMinor, 0),
    total: true,
  });
  return rows;
}

function inputTableRows(report: VatReturn): InputTableRow[] {
  const rates = allInputRates(report.input, report.purchaseReturns, report.voidedReceipts);

  const rows: InputTableRow[] = rates.map((rate) => {
    const received = report.input.find((row) => row.rate === rate);
    const returned = report.purchaseReturns.find((row) => row.rate === rate);
    return {
      key: String(rate),
      label: `${rate}%`,
      receipts: received?.documents ?? 0,
      receivedTaxableMinor: received?.taxableMinor ?? 0,
      receivedTaxMinor: received?.taxMinor ?? 0,
      returnNotes: returned?.documents ?? 0,
      returnedTaxableMinor: returned?.taxableMinor ?? 0,
      returnedTaxMinor: returned?.taxMinor ?? 0,
      voidedTaxableMinor: voidedFigureAt(report.voidedReceipts, rate)?.taxableMinor ?? 0,
      voidedTaxMinor: voidedFigureAt(report.voidedReceipts, rate)?.taxMinor ?? 0,
      total: false,
    };
  });
  rows.push({
    key: 'total',
    label: tr('pharmacy', 'total'),
    receipts: null,
    receivedTaxableMinor: report.inputTotals.taxableMinor,
    receivedTaxMinor: report.inputTotals.taxMinor,
    returnNotes: null,
    returnedTaxableMinor: report.purchaseReturnTotals.taxableMinor,
    returnedTaxMinor: report.purchaseReturnTotals.taxMinor,
    voidedTaxableMinor: report.voidedReceiptTotals?.taxableMinor ?? 0,
    voidedTaxMinor: report.voidedReceiptTotals?.taxMinor ?? 0,
    total: true,
  });
  return rows;
}

function voidedTableRows(report: VatReturn): VoidedTableRow[] {
  const rows: VoidedTableRow[] = (report.voidedReceipts ?? []).map((row) => ({
    key: String(row.rate),
    label: `${row.rate}%`,
    receipts: row.documents,
    taxableMinor: row.taxableMinor,
    taxMinor: row.taxMinor,
    total: false,
  }));
  if (rows.length > 0) {
    rows.push({
      key: 'total',
      label: tr('pharmacy', 'total'),
      receipts: null,
      taxableMinor: report.voidedReceiptTotals?.taxableMinor ?? 0,
      taxMinor: report.voidedReceiptTotals?.taxMinor ?? 0,
      total: true,
    });
  }
  return rows;
}

function vatCsvRows(report: VatReturn, inputRows: InputTableRow[]): VatCsvRow[] {
  const blank = { documents: null, taxableMinor: null, taxMinor: null, debitMinor: null, creditMinor: null };
  return [
    ...report.output.map((row) => ({
      ...blank,
      section: 'Output VAT (after returns)',
      line: `${row.rate}%`,
      documents: row.documents,
      taxableMinor: row.taxableMinor,
      taxMinor: row.taxMinor,
    })),
    ...inputRows
      .filter((row) => !row.total)
      .map((row) => ({
        ...blank,
        section: 'Input VAT (goods received less returns and voids)',
        line: row.label,
        documents: row.receipts,
        taxableMinor: row.receivedTaxableMinor - row.returnedTaxableMinor - row.voidedTaxableMinor,
        taxMinor: netInputTaxMinor(row),
      })),
    ...(report.voidedReceipts ?? []).map((row) => ({
      ...blank,
      section: 'Goods received voided',
      line: `${row.rate}%`,
      documents: row.documents,
      taxableMinor: row.taxableMinor,
      taxMinor: row.taxMinor,
    })),
    { ...blank, section: 'Net VAT payable', line: 'From documents', taxMinor: report.documents.netVatPayableMinor },
    { ...blank, section: 'Net VAT payable', line: 'From ledger', taxMinor: report.ledger.netVatPayableMinor },
    { ...blank, section: 'Ledger 2400', line: 'Opening balance', taxMinor: report.ledger.openingBalanceMinor },
    ...report.ledger.sources.map((row) => ({
      ...blank,
      section: 'Ledger 2400',
      line: `${humanize(row.source)} (${row.class})`,
      debitMinor: row.debitMinor,
      creditMinor: row.creditMinor,
    })),
    { ...blank, section: 'Ledger 2400', line: 'Closing balance', taxMinor: report.ledger.closingBalanceMinor },
  ];
}

function DifferenceCell({ minor }: { minor: number }) {
  if (minor === 0) return <span className="tabular text-muted-foreground">{money(0)}</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <Badge variant="destructive">{tr('pharmacy', 'differs')}</Badge>
      <Amount minor={minor} strong tone="bad" />
    </span>
  );
}

function vatExportRows(report: VatReturn): VatCsvRow[] {
  return vatCsvRows(report, inputTableRows(report));
}

function VatReturnView({ report }: { report: VatReturn }) {
  const { documents, ledger, reconciliation } = report;
  const inputRows = inputTableRows(report);
  const disagrees = reconciliation.outputDifferenceMinor !== 0 || reconciliation.inputDifferenceMinor !== 0;

  const comparison: ComparisonRow[] = [
    {
      label: tr('pharmacy', 'output_vat_charged_on_sales_after_returns'),
      documentsMinor: documents.outputTaxMinor,
      ledgerMinor: ledger.outputTaxMinor,
      differenceMinor: reconciliation.outputDifferenceMinor,
      strong: false,
    },
    {
      label: tr('pharmacy', 'input_vat_paid_on_goods_received_after_returns'),
      documentsMinor: documents.inputTaxMinor,
      ledgerMinor: ledger.inputTaxMinor,
      differenceMinor: reconciliation.inputDifferenceMinor,
      strong: false,
    },
    {
      label: tr('pharmacy', 'net_vat_payable'),
      documentsMinor: documents.netVatPayableMinor,
      ledgerMinor: ledger.netVatPayableMinor,
      differenceMinor: ledger.netVatPayableMinor - documents.netVatPayableMinor,
      strong: true,
    },
  ];

  const outputColumns: Column<OutputTableRow>[] = [
    { header: tr('pharmacy', 'rate'), cell: (row) => <span className={cn(row.total && 'font-semibold')}>{row.label}</span> },
    { header: tr('pharmacy', 'invoices'), align: 'right', cell: (row) => row.documents ?? '—' },
    { header: tr('pharmacy', 'taxable_sales'), align: 'right', cell: (row) => <Amount minor={row.taxableMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'output_vat'), align: 'right', cell: (row) => <Amount minor={row.taxMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'returned_taxable'), align: 'right', cell: (row) => <Amount minor={row.returnedTaxableMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'returned_vat'), align: 'right', cell: (row) => <Amount minor={row.returnedTaxMinor} strong={row.total} /> },
  ];

  const inputColumns: Column<InputTableRow>[] = [
    { header: tr('pharmacy', 'rate'), cell: (row) => <span className={cn(row.total && 'font-semibold')}>{row.label}</span> },
    { header: tr('pharmacy', 'receipts'), align: 'right', cell: (row) => row.receipts ?? '—' },
    { header: tr('pharmacy', 'received_taxable'), align: 'right', cell: (row) => <Amount minor={row.receivedTaxableMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'received_vat'), align: 'right', cell: (row) => <Amount minor={row.receivedTaxMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'return_notes'), align: 'right', cell: (row) => row.returnNotes ?? '—' },
    { header: tr('pharmacy', 'returned_taxable'), align: 'right', cell: (row) => <Amount minor={row.returnedTaxableMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'returned_vat'), align: 'right', cell: (row) => <Amount minor={row.returnedTaxMinor} strong={row.total} /> },
    {
      header: tr('pharmacy', 'net_input_vat'),
      align: 'right',
      cell: (row) => <Amount minor={netInputTaxMinor(row)} strong />,
    },
  ];

  const voidedColumns: Column<VoidedTableRow>[] = [
    { header: tr('pharmacy', 'rate'), cell: (row) => <span className={cn(row.total && 'font-semibold')}>{row.label}</span> },
    { header: tr('pharmacy', 'receipts_voided'), align: 'right', cell: (row) => row.receipts ?? '—' },
    { header: tr('pharmacy', 'voided_taxable'), align: 'right', cell: (row) => <Amount minor={row.taxableMinor} strong={row.total} /> },
    { header: tr('pharmacy', 'voided_vat'), align: 'right', cell: (row) => <Amount minor={row.taxMinor} strong={row.total} /> },
  ];

  const ledgerColumns: Column<VatLedgerSource>[] = [
    { header: tr('pharmacy', 'posted_by'), sort: (row) => row.source, cell: (row) => humanize(row.source) },
    {
      header: tr('pharmacy', 'counts_as'),
      sort: (row) => row.class,
      cell: (row) =>
        row.class === 'other' ? (
          <Badge variant="muted">{tr('pharmacy', 'other_not_vat')}</Badge>
        ) : (
          <Badge variant={row.class === 'output' ? 'default' : 'secondary'}>{humanize(row.class)} {tr('pharmacy', 'vat')}</Badge>
        ),
    },
    { header: tr('pharmacy', 'debit'), align: 'right', sort: (row) => row.debitMinor, cell: (row) => <Amount minor={row.debitMinor} /> },
    { header: tr('pharmacy', 'credit'), align: 'right', sort: (row) => row.creditMinor, cell: (row) => <Amount minor={row.creditMinor} /> },
    { header: tr('pharmacy', 'net_credit'), align: 'right', sort: (row) => row.netCreditMinor, cell: (row) => <Amount minor={row.netCreditMinor} strong /> },
  ];

  const ledgerSummary = [
    { label: tr('pharmacy', 'opening_balance_before_x', { from: dateOnly(report.from) }), minor: ledger.openingBalanceMinor, strong: false },
    { label: tr('pharmacy', 'output_vat_posted'), minor: ledger.outputTaxMinor, strong: false },
    { label: tr('pharmacy', 'input_vat_posted_reduces_the_balance'), minor: -ledger.inputTaxMinor, strong: false },
    { label: tr('pharmacy', 'other_postings_not_vat'), minor: ledger.otherMinor, strong: false },
    { label: tr('pharmacy', 'movement_in_the_period'), minor: ledger.movementMinor, strong: true },
    { label: tr('pharmacy', 'closing_balance_at_x', { to: dateOnly(report.to) }), minor: ledger.closingBalanceMinor, strong: true },
  ];

  return (
    <div className="space-y-6">
      {disagrees ? (
        <Alert variant="destructive" title={tr('pharmacy', 'the_ledger_does_not_match_the_documents')}>
          {tr('pharmacy', 'output_vat_differs_by')} {money(reconciliation.outputDifferenceMinor)} {tr('pharmacy', 'and_input_vat_differs_by')}{' '}
          {money(reconciliation.inputDifferenceMinor)} {tr('pharmacy', 'ledger_minus_documents_look_at_the_ledger')}
        </Alert>
      ) : (
        <Alert variant="success">
          {tr('pharmacy', 'ledger_account')} {ledger.account} {tr('pharmacy', 'agrees_with_the_invoices_and_goods_received_for')}
        </Alert>
      )}

      <Section
        title={tr('pharmacy', 'net_vat_payable_two_ways')}
        note={`${dateOnly(report.from)} to ${dateOnly(report.to)}. The difference is ledger minus documents.`}
      >
        <DataTable
          rows={comparison}
          rowKey={(row) => row.label}
          columns={[
            { header: tr('pharmacy', 'line_2'), cell: (row) => <span className={cn(row.strong && 'font-semibold')}>{row.label}</span> },
            {
              header: tr('pharmacy', 'from_invoices_and_goods_received'),
              align: 'right',
              cell: (row) => <Amount minor={row.documentsMinor} strong={row.strong} />,
            },
            {
              header: tr('pharmacy', 'from_ledger_x', { account: ledger.account }),
              align: 'right',
              cell: (row) => <Amount minor={row.ledgerMinor} strong={row.strong} />,
            },
            { header: tr('pharmacy', 'difference'), align: 'right', cell: (row) => <DifferenceCell minor={row.differenceMinor} /> },
          ]}
        />
      </Section>

      <Section
        title={tr('pharmacy', 'output_vat_by_rate')}
        note="Taxable sales and VAT are after customer returns; the returned columns show how much that was."
      >
        <DataTable
          rows={outputTableRows(report)}
          rowKey={(row) => row.key}
          empty={tr('pharmacy', 'no_sales_with_vat_were_invoiced_in_this_period')}
          columns={outputColumns}
        />
      </Section>

      <Section
        title={tr('pharmacy', 'input_vat_by_rate')}
        note="Goods received less what was returned to suppliers and what was voided, shown in the table below."
      >
        <DataTable
          rows={inputRows}
          rowKey={(row) => row.key}
          empty={tr('pharmacy', 'no_goods_were_received_in_this_period')}
          columns={inputColumns}
        />
      </Section>

      <Section
        title={tr('pharmacy', 'goods_received_voided')}
        note="Receipts cancelled in this period count against input VAT in the same way as goods returned to suppliers."
      >
        <DataTable
          rows={voidedTableRows(report)}
          rowKey={(row) => row.key}
          empty={tr('pharmacy', 'no_goods_received_were_voided_in_this_period')}
          columns={voidedColumns}
        />
      </Section>

      <Section title={tr('pharmacy', 'ledger_account_x', { account: ledger.account })} note="Credit-positive: an amount owed to the tax authority shows as a positive balance.">
        <dl className="divide-y rounded-lg border text-sm">
          {ledgerSummary.map((row) => (
            <div
              key={row.label}
              className={cn('flex items-center justify-between gap-3 px-3 py-2', row.strong && 'bg-muted/50')}
            >
              <dt className={cn(row.strong && 'font-semibold')}>{row.label}</dt>
              <dd>
                <Amount minor={row.minor} strong={row.strong} />
              </dd>
            </div>
          ))}
        </dl>
        <Alert>
          {tr('pharmacy', 'account')} {ledger.account} {tr('pharmacy', 'also_receives_payroll_and_doctor_withholding')}
        </Alert>
        <DataTable
          rows={ledger.sources}
          rowKey={(row) => `${row.source}-${row.class}`}
          empty={tr('pharmacy', 'nothing_was_posted_to_account_x_in_this_period', { account: ledger.account })}
          columns={ledgerColumns}
        />
      </Section>
    </div>
  );
}

export function VatReturnReport() {
  const filters = useReportFilters(() => ({ from: isoDaysBack(DEFAULT_PERIOD_DAYS - 1), to: todayIso() }));
  const { applied } = filters;
  const search = qs({ from: applied?.values.from, to: applied?.values.to });

  const report = useQuery({
    queryKey: ['pharmacy-report', 'vat-return', search, applied?.runs],
    queryFn: () => get<VatReturn>(`/pharmacy/reports/vat-return${search}`),
    enabled: applied !== null,
  });

  return (
    <>
      <ReportFilterBar
        from={filters.draft.from}
        to={filters.draft.to}
        onChange={filters.change}
        running={report.isFetching}
        onRun={filters.run}
        trailing={
          report.data && (
            <ExportButton
              filename={`vat-return-${report.data.from}-to-${report.data.to}`}
              rows={vatExportRows(report.data)}
              columns={VAT_CSV_COLUMNS}
            />
          )
        }
      />
      <ReportBody started={applied !== null} loading={report.isLoading} error={report.error}>
        {report.data && <VatReturnView report={report.data} />}
      </ReportBody>
    </>
  );
}

type Grouping = 'product' | 'category';

const GROUPINGS: { value: Grouping; label: string }[] = [
  { value: 'product', label: 'By product' },
  { value: 'category', label: 'By category' },
];

interface SalesLine {
  key: string;
  code: string | null;
  name: string;
  category: string;
  qty: number;
  revenueMinor: number;
  costMinor: number;
  marginMinor: number;
  marginPct: number | null;
}

interface SalesReport {
  from: string;
  to: string;
  groupBy: Grouping;
  totalRows: number;
  totals: { revenueMinor: number; costMinor: number; marginMinor: number; marginPct: number | null };
  lines: SalesLine[];
}

function salesCsvColumns(groupBy: Grouping): CsvColumn<SalesLine>[] {
  const identity: CsvColumn<SalesLine>[] =
    groupBy === 'product'
      ? [
          { header: tr('pharmacy', 'code'), value: (row) => row.code },
          { header: tr('pharmacy', 'product'), value: (row) => row.name },
          { header: tr('pharmacy', 'category'), value: (row) => row.category },
        ]
      : [{ header: tr('pharmacy', 'category'), value: (row) => row.name }];

  return [
    ...identity,
    { header: tr('pharmacy', 'quantity_sold'), value: (row) => row.qty },
    { header: tr('pharmacy', 'revenue'), value: (row) => plainAmount(row.revenueMinor) },
    { header: tr('pharmacy', 'cost'), value: (row) => plainAmount(row.costMinor) },
    { header: tr('pharmacy', 'margin'), value: (row) => plainAmount(row.marginMinor) },
    { header: tr('pharmacy', 'margin_2'), value: (row) => row.marginPct },
  ];
}

function GroupingToggle({ value, onChange }: { value: Grouping; onChange: (value: Grouping) => void }) {
  return (
    <FilterField label={tr('pharmacy', 'group_by')}>
      <div className="flex gap-1" role="group" aria-label={tr('pharmacy', 'group_by')}>
        {GROUPINGS.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant={value === option.value ? 'default' : 'outline'}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </FilterField>
  );
}

function SalesView({
  report,
  offset,
  busy,
  onPage,
}: {
  report: SalesReport;
  offset: number;
  busy: boolean;
  onPage: (offset: number) => void;
}) {
  const { totals } = report;
  const byProduct = report.groupBy === 'product';

  const columns: Column<SalesLine>[] = [
    ...(byProduct
      ? [{ header: tr('pharmacy', 'code'), width: '110px', sort: (row: SalesLine) => row.code, cell: (row: SalesLine) => <span className="font-mono text-xs">{row.code ?? '—'}</span> }]
      : []),
    { header: byProduct ? tr('pharmacy', 'product') : tr('pharmacy', 'category'), sort: (row) => row.name, cell: (row) => row.name },
    ...(byProduct ? [{ header: tr('pharmacy', 'category'), sort: (row: SalesLine) => row.category, cell: (row: SalesLine) => row.category }] : []),
    { header: tr('pharmacy', 'qty_sold'), align: 'right', sort: (row) => row.qty, cell: (row) => quantity(row.qty) },
    { header: tr('pharmacy', 'revenue'), align: 'right', sort: (row) => row.revenueMinor, cell: (row) => <Amount minor={row.revenueMinor} /> },
    { header: tr('pharmacy', 'cost'), align: 'right', sort: (row) => row.costMinor, cell: (row) => <Amount minor={row.costMinor} /> },
    {
      header: tr('pharmacy', 'margin'),
      align: 'right',
      sort: (row) => row.marginMinor,
      cell: (row) => <Amount minor={row.marginMinor} strong tone={row.marginMinor < 0 ? 'bad' : undefined} />,
    },
    { header: tr('pharmacy', 'margin_2'), align: 'right', sort: (row) => row.marginPct, cell: (row) => percent(row.marginPct) },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={tr('pharmacy', 'revenue')} value={money(totals.revenueMinor)} sub={tr('pharmacy', 'before_tax_after_returns')} />
        <StatTile label={tr('pharmacy', 'cost_of_goods')} value={money(totals.costMinor)} sub={tr('pharmacy', 'batch_cost_at_the_time_of_sale')} />
        <StatTile
          label={tr('pharmacy', 'margin')}
          value={money(totals.marginMinor)}
          tone={totals.marginMinor < 0 ? 'destructive' : 'default'}
        />
        <StatTile label={tr('pharmacy', 'margin_2')} value={percent(totals.marginPct)} sub={tr('pharmacy', 'totals_cover_every_row_not_only_this_page')} />
      </div>

      <Section
        title={byProduct ? tr('pharmacy', 'sales_by_product') : tr('pharmacy', 'sales_by_category')}
        note={`${dateOnly(report.from)} to ${dateOnly(report.to)}, largest revenue first`}
      >
        <DataTable
          rows={report.lines}
          rowKey={(row) => row.key}
          empty={tr('pharmacy', 'nothing_was_sold_in_this_period')}
          columns={columns}
          footer={<Pager offset={offset} total={report.totalRows} shown={report.lines.length} busy={busy} onChange={onPage} />}
        />
      </Section>
    </div>
  );
}

export function SalesMarginReport() {
  const filters = useReportFilters(() => ({
    from: isoDaysBack(DEFAULT_PERIOD_DAYS - 1),
    to: todayIso(),
    groupBy: 'product' as Grouping,
    storeId: '',
  }));
  const { applied, offset } = filters;
  const filterParams: QueryParams = {
    from: applied?.values.from,
    to: applied?.values.to,
    groupBy: applied?.values.groupBy,
    storeId: applied?.values.storeId,
  };
  const search = qs({ ...filterParams, limit: PAGE_SIZE, offset });

  const report = useQuery({
    queryKey: ['pharmacy-report', 'sales-by-product', search, applied?.runs],
    queryFn: () => get<SalesReport>(`/pharmacy/reports/sales-by-product${search}`),
    enabled: applied !== null,
    placeholderData: keepWhilePaging<SalesReport>(applied?.runs),
  });

  return (
    <>
      <ReportFilterBar
        from={filters.draft.from}
        to={filters.draft.to}
        onChange={filters.change}
        running={report.isFetching}
        onRun={filters.run}
        trailing={
          report.data && (
            <CsvExportButton<SalesReport, SalesLine>
              path="/pharmacy/reports/sales-by-product"
              params={filterParams}
              pickRows={(response) => response.lines}
              columns={salesCsvColumns(report.data.groupBy)}
              filename={`sales-margin-${report.data.groupBy}-${report.data.from}-to-${report.data.to}`}
              disabled={report.data.lines.length === 0}
            />
          )
        }
      >
        <GroupingToggle value={filters.draft.groupBy} onChange={(groupBy) => filters.change({ groupBy })} />
        <StoreFilter value={filters.draft.storeId} onChange={(storeId) => filters.change({ storeId })} />
      </ReportFilterBar>
      <ReportBody
        started={applied !== null}
        loading={report.isLoading}
        error={report.error}
        dimmed={report.isPlaceholderData}
      >
        {report.data && (
          <SalesView report={report.data} offset={offset} busy={report.isFetching} onPage={filters.setOffset} />
        )}
      </ReportBody>
    </>
  );
}

interface ExpiryCategory {
  category: string | null;
  label: string;
  movements: number;
  qty: number;
  valueMinor: number;
}

interface ExpiryLine {
  at: string;
  code: string;
  name: string;
  store: string;
  batchNo: string;
  expiryDate: string;
  qty: number;
  unitCostMinor: number;
  valueMinor: number;
  reason: string | null;
}

interface ExpiryLossData {
  from: string;
  to: string;
  writtenOffAsExpiredMinor: number;
  allLossesMinor: number;
  returnedToSuppliersAsExpiredMinor: number;
  stillOnShelfExpired: { batches: number; valueMinor: number };
  byCategory: ExpiryCategory[];
  ledger?: ExpiryLedgerTie | null;
  totalRows: number;
  lines: ExpiryLine[];
}

const EXPIRY_CSV_COLUMNS: CsvColumn<ExpiryLine>[] = [
  { header: 'Written off', value: (row) => dateTime(row.at) },
  { header: 'Code', value: (row) => row.code },
  { header: 'Product', value: (row) => row.name },
  { header: 'Store', value: (row) => row.store },
  { header: 'Batch', value: (row) => row.batchNo },
  { header: 'Expiry date', value: (row) => dateOnly(row.expiryDate) },
  { header: 'Quantity', value: (row) => row.qty },
  { header: 'Unit cost', value: (row) => plainAmount(row.unitCostMinor) },
  { header: 'Value', value: (row) => plainAmount(row.valueMinor) },
  { header: 'Reason', value: (row) => row.reason },
];

function ExpiryLossView({
  report,
  offset,
  busy,
  onPage,
}: {
  report: ExpiryLossData;
  offset: number;
  busy: boolean;
  onPage: (offset: number) => void;
}) {
  const shelf = report.stillOnShelfExpired;
  const ledger = report.ledger ?? null;

  const categoryColumns: Column<ExpiryCategory>[] = [
    { header: tr('pharmacy', 'loss_category'), sort: (row) => row.label, cell: (row) => row.label },
    { header: tr('pharmacy', 'movements'), align: 'right', sort: (row) => row.movements, cell: (row) => row.movements },
    { header: tr('pharmacy', 'quantity'), align: 'right', sort: (row) => row.qty, cell: (row) => quantity(row.qty) },
    { header: tr('pharmacy', 'value_at_cost'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => <Amount minor={row.valueMinor} strong /> },
  ];

  const lineColumns: Column<ExpiryLine>[] = [
    { header: tr('pharmacy', 'written_off'), sort: (row) => row.at, cell: (row) => dateTime(row.at) },
    {
      header: tr('pharmacy', 'product'),
      sort: (row) => row.name,
      cell: (row) => (
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="font-mono text-xs text-muted-foreground">{row.code}</p>
        </div>
      ),
    },
    { header: tr('pharmacy', 'store'), sort: (row) => row.store, cell: (row) => row.store },
    { header: tr('pharmacy', 'batch'), sort: (row) => row.batchNo, cell: (row) => <span className="font-mono text-xs">{row.batchNo}</span> },
    { header: tr('pharmacy', 'expired_on'), sort: (row) => row.expiryDate, cell: (row) => dateOnly(row.expiryDate) },
    { header: tr('pharmacy', 'qty_2'), align: 'right', sort: (row) => row.qty, cell: (row) => quantity(row.qty) },
    { header: tr('pharmacy', 'unit_cost'), align: 'right', sort: (row) => row.unitCostMinor, cell: (row) => <Amount minor={row.unitCostMinor} /> },
    { header: tr('pharmacy', 'value'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => <Amount minor={row.valueMinor} strong /> },
    { header: tr('pharmacy', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason ?? '—' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={tr('pharmacy', 'written_off_as_expired')}
          value={money(report.writtenOffAsExpiredMinor)}
          sub={tr('pharmacy', 'at_cost_in_this_period')}
        />
        <StatTile
          label={tr('pharmacy', 'all_stock_losses')}
          value={money(report.allLossesMinor)}
          sub={tr('pharmacy', 'expired_damaged_theft_count_differences_and')}
        />
        <StatTile
          label={tr('pharmacy', 'returned_to_suppliers')}
          value={money(report.returnedToSuppliersAsExpiredMinor)}
          sub={tr('pharmacy', 'sent_back_as_expired_in_this_period')}
        />
        <StatTile
          label={tr('pharmacy', 'still_on_the_shelf_expired')}
          value={money(shelf.valueMinor)}
          sub={`${plural(shelf.batches, 'batch', 'batches')}, as of today`}
          tone={shelf.batches > 0 ? 'destructive' : 'default'}
        />
      </div>

      <Section title={tr('pharmacy', 'losses_by_category')} note="Every stock write-off in the period, whatever the reason, including count differences.">
        <DataTable
          rows={report.byCategory}
          rowKey={(row) => row.category ?? 'unclassified'}
          empty={tr('pharmacy', 'no_stock_was_written_off_in_this_period')}
          columns={categoryColumns}
        />
        <p className={cn('text-xs text-muted-foreground', ledger !== null && ledger.otherPostingsMinor !== 0 && 'text-warning')}>
          {ledgerTieLine(ledger, money)}
        </p>
      </Section>

      <Section
        title={tr('pharmacy', 'written_off_as_expired')}
        note={`${dateOnly(report.from)} to ${dateOnly(report.to)}, newest first`}
      >
        <DataTable
          rows={report.lines}
          rowKey={(row) => [row.at, row.code, row.batchNo, row.qty, row.reason].join('|')}
          empty={tr('pharmacy', 'no_stock_was_written_off_as_expired_in_this')}
          columns={lineColumns}
          footer={<Pager offset={offset} total={report.totalRows} shown={report.lines.length} busy={busy} onChange={onPage} />}
        />
      </Section>
    </div>
  );
}

export function ExpiryLossReport() {
  const filters = useReportFilters(() => ({
    from: isoDaysBack(EXPIRY_PERIOD_DAYS - 1),
    to: todayIso(),
    storeId: '',
  }));
  const { applied, offset } = filters;
  const filterParams: QueryParams = {
    from: applied?.values.from,
    to: applied?.values.to,
    storeId: applied?.values.storeId,
  };
  const search = qs({ ...filterParams, limit: PAGE_SIZE, offset });

  const report = useQuery({
    queryKey: ['pharmacy-report', 'expiry-loss', search, applied?.runs],
    queryFn: () => get<ExpiryLossData>(`/pharmacy/reports/expiry-loss${search}`),
    enabled: applied !== null,
    placeholderData: keepWhilePaging<ExpiryLossData>(applied?.runs),
  });

  return (
    <>
      <ReportFilterBar
        from={filters.draft.from}
        to={filters.draft.to}
        onChange={filters.change}
        running={report.isFetching}
        onRun={filters.run}
        trailing={
          report.data && (
            <CsvExportButton<ExpiryLossData, ExpiryLine>
              path="/pharmacy/reports/expiry-loss"
              params={filterParams}
              pickRows={(response) => response.lines}
              columns={EXPIRY_CSV_COLUMNS}
              filename={`expiry-loss-${report.data.from}-to-${report.data.to}`}
              disabled={report.data.lines.length === 0}
            />
          )
        }
      >
        <StoreFilter value={filters.draft.storeId} onChange={(storeId) => filters.change({ storeId })} />
      </ReportFilterBar>
      <ReportBody
        started={applied !== null}
        loading={report.isLoading}
        error={report.error}
        dimmed={report.isPlaceholderData}
      >
        {report.data && (
          <ExpiryLossView report={report.data} offset={offset} busy={report.isFetching} onPage={filters.setOffset} />
        )}
      </ReportBody>
    </>
  );
}

const MOVEMENT_LABELS: Record<string, string> = {
  grn: 'Goods received',
  sale: 'Sold',
  indent_issue: 'Issued to wards',
  return: 'Returned by customers',
  transfer_in: 'Transferred in',
  transfer_out: 'Transferred out',
  adjustment: 'Adjustments',
  write_off: 'Written off',
  purchase_return: 'Returned to suppliers',
};

interface MovementType {
  type: string;
  movements: number;
  qty: number;
  valueMinor: number;
}

interface MovementProduct {
  productId: string;
  code: string;
  name: string;
  received: number;
  receivedValueMinor: number;
  voidedReceipts: number;
  sold: number;
  wardIssued: number;
  returnedIn: number;
  transferIn: number;
  transferOut: number;
  adjusted: number;
  writtenOff: number;
  returnedToSupplier: number;
  netQty: number;
  netValueMinor: number;
  onHand: number;
}

interface MovementSummary {
  from: string;
  to: string;
  totalRows: number;
  totalsByType: MovementType[];
  products: MovementProduct[];
}

const MOVEMENT_CSV_COLUMNS: CsvColumn<MovementProduct>[] = [
  { header: 'Code', value: (row) => row.code },
  { header: 'Product', value: (row) => row.name },
  { header: 'Received', value: (row) => row.received },
  { header: 'Received value', value: (row) => plainAmount(row.receivedValueMinor) },
  { header: 'Receipts voided', value: (row) => row.voidedReceipts },
  { header: 'Sold', value: (row) => row.sold },
  { header: 'Issued to wards', value: (row) => row.wardIssued },
  { header: 'Returned by customers', value: (row) => row.returnedIn },
  { header: 'Transferred in', value: (row) => row.transferIn },
  { header: 'Transferred out', value: (row) => row.transferOut },
  { header: 'Adjusted', value: (row) => row.adjusted },
  { header: 'Written off', value: (row) => row.writtenOff },
  { header: 'Returned to suppliers', value: (row) => row.returnedToSupplier },
  { header: 'Net quantity', value: (row) => row.netQty },
  { header: 'Net value', value: (row) => plainAmount(row.netValueMinor) },
  { header: 'On hand now', value: (row) => row.onHand },
];

function MovementSummaryView({
  report,
  offset,
  busy,
  onPage,
}: {
  report: MovementSummary;
  offset: number;
  busy: boolean;
  onPage: (offset: number) => void;
}) {
  const typeColumns: Column<MovementType>[] = [
    { header: tr('pharmacy', 'movement'), sort: (row) => row.type, cell: (row) => MOVEMENT_LABELS[row.type] ?? humanize(row.type) },
    { header: tr('pharmacy', 'movements'), align: 'right', sort: (row) => row.movements, cell: (row) => row.movements },
    { header: tr('pharmacy', 'net_quantity'), align: 'right', sort: (row) => row.qty, cell: (row) => quantity(row.qty) },
    { header: tr('pharmacy', 'net_value_at_cost'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => <Amount minor={row.valueMinor} strong /> },
  ];

  const productColumns: Column<MovementProduct>[] = [
    { header: tr('pharmacy', 'code'), width: '100px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
    { header: tr('pharmacy', 'product'), sort: (row) => row.name, cell: (row) => row.name },
    { header: tr('pharmacy', 'received'), align: 'right', sort: (row) => row.received, cell: (row) => quantityOrDash(row.received) },
    {
      header: tr('pharmacy', 'received_value'),
      align: 'right',
      sort: (row) => row.receivedValueMinor,
      cell: (row) => (row.receivedValueMinor === 0 ? '—' : <Amount minor={row.receivedValueMinor} />),
    },
    { header: tr('pharmacy', 'receipts_voided'), align: 'right', sort: (row) => row.voidedReceipts, cell: (row) => quantityOrDash(row.voidedReceipts) },
    { header: tr('pharmacy', 'sold'), align: 'right', sort: (row) => row.sold, cell: (row) => quantityOrDash(row.sold) },
    { header: tr('pharmacy', 'to_wards'), align: 'right', sort: (row) => row.wardIssued, cell: (row) => quantityOrDash(row.wardIssued) },
    { header: tr('pharmacy', 'returned_in'), align: 'right', sort: (row) => row.returnedIn, cell: (row) => quantityOrDash(row.returnedIn) },
    { header: tr('pharmacy', 'transfer_in'), align: 'right', sort: (row) => row.transferIn, cell: (row) => quantityOrDash(row.transferIn) },
    { header: tr('pharmacy', 'transfer_out'), align: 'right', sort: (row) => row.transferOut, cell: (row) => quantityOrDash(row.transferOut) },
    { header: tr('pharmacy', 'adjusted'), align: 'right', sort: (row) => row.adjusted, cell: (row) => quantityOrDash(row.adjusted) },
    { header: tr('pharmacy', 'written_off'), align: 'right', sort: (row) => row.writtenOff, cell: (row) => quantityOrDash(row.writtenOff) },
    { header: tr('pharmacy', 'to_supplier'), align: 'right', sort: (row) => row.returnedToSupplier, cell: (row) => quantityOrDash(row.returnedToSupplier) },
    { header: tr('pharmacy', 'net_qty'), align: 'right', sort: (row) => row.netQty, cell: (row) => <strong>{quantity(row.netQty)}</strong> },
    { header: tr('pharmacy', 'net_value'), align: 'right', sort: (row) => row.netValueMinor, cell: (row) => <Amount minor={row.netValueMinor} /> },
    { header: tr('pharmacy', 'on_hand_now'), align: 'right', sort: (row) => row.onHand, cell: (row) => quantity(row.onHand) },
  ];

  return (
    <div className="space-y-6">
      <Section
        title={tr('pharmacy', 'totals_by_movement_type')}
        note="Signed: stock that left shows as negative. Every movement in the period, not only the products on this page."
      >
        <DataTable
          rows={report.totalsByType}
          rowKey={(row) => row.type}
          empty={tr('pharmacy', 'no_stock_moved_in_this_period')}
          columns={typeColumns}
        />
      </Section>

      <Section
        title={tr('pharmacy', 'by_product')}
        note={`${dateOnly(report.from)} to ${dateOnly(report.to)}, biggest movement first. On hand is the quantity in stock now, not at the end of the period.`}
      >
        <DataTable
          dense
          rows={report.products}
          rowKey={(row) => row.productId}
          empty={tr('pharmacy', 'no_product_had_any_stock_movement_in_this_period')}
          columns={productColumns}
          footer={<Pager offset={offset} total={report.totalRows} shown={report.products.length} busy={busy} onChange={onPage} />}
        />
      </Section>
    </div>
  );
}

export function StockMovementReport() {
  const filters = useReportFilters(() => ({
    from: isoDaysBack(DEFAULT_PERIOD_DAYS - 1),
    to: todayIso(),
    storeId: '',
  }));
  const { applied, offset } = filters;
  const filterParams: QueryParams = {
    from: applied?.values.from,
    to: applied?.values.to,
    storeId: applied?.values.storeId,
  };
  const search = qs({ ...filterParams, limit: PAGE_SIZE, offset });

  const report = useQuery({
    queryKey: ['pharmacy-report', 'movement-summary', search, applied?.runs],
    queryFn: () => get<MovementSummary>(`/pharmacy/reports/movement-summary${search}`),
    enabled: applied !== null,
    placeholderData: keepWhilePaging<MovementSummary>(applied?.runs),
  });

  return (
    <>
      <ReportFilterBar
        from={filters.draft.from}
        to={filters.draft.to}
        onChange={filters.change}
        running={report.isFetching}
        onRun={filters.run}
        trailing={
          report.data && (
            <CsvExportButton<MovementSummary, MovementProduct>
              path="/pharmacy/reports/movement-summary"
              params={filterParams}
              pickRows={(response) => response.products}
              columns={MOVEMENT_CSV_COLUMNS}
              filename={`stock-movement-${report.data.from}-to-${report.data.to}`}
              disabled={report.data.products.length === 0}
            />
          )
        }
      >
        <StoreFilter value={filters.draft.storeId} onChange={(storeId) => filters.change({ storeId })} />
      </ReportFilterBar>
      <ReportBody
        started={applied !== null}
        loading={report.isLoading}
        error={report.error}
        dimmed={report.isPlaceholderData}
      >
        {report.data && (
          <MovementSummaryView report={report.data} offset={offset} busy={report.isFetching} onPage={filters.setOffset} />
        )}
      </ReportBody>
    </>
  );
}
